import { forwardRef } from 'react';

import { HeadingContext } from 'react-aria-components/Heading';
import { DEFAULT_SLOT, Provider } from 'react-aria-components/slots';

import { useSlotId } from '../../hooks/use_slot_id';
import { cx } from '../../utils/cx';

import styles from './panel.module.css';

export type PanelProps = React.PropsWithChildren<
  React.HTMLAttributes<HTMLDivElement>
>;

/**
 * Panel - A container component with header/content structure.
 *
 * Will be automatically labeled by `PanelHeading`, and should otherwise
 * be labeled via `aria-label` or `aria-labelledby`.
 *
 * ```tsx
 * <Panel>
 *   <PanelHeader isClosable onClose={() => {}}>
 *     <PanelHeading>Panel Header</PanelHeading>
 *   </PanelHeader>
 *   <PanelContent>Panel Body</PanelContent>
 * </Panel>
 * ```
 */
export const Panel = forwardRef<HTMLDivElement, PanelProps>(
  ({ className, children, ...rest }, ref) => {
    const panelHeadingId = useSlotId();
    let labeledBy = panelHeadingId;
    if (rest['aria-labelledby'] || rest['aria-label']) {
      labeledBy = undefined;
    }

    return (
      <section
        ref={ref}
        className={cx([styles.panel, className])}
        aria-labelledby={labeledBy}
        {...rest}
      >
        <Provider
          values={[
            [
              HeadingContext,
              {
                slots: {
                  [DEFAULT_SLOT]: {},
                  title: { id: panelHeadingId },
                },
              },
            ],
          ]}
        >
          {children}
        </Provider>
      </section>
    );
  },
);
